// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Phase 3i: persistent (IndexedDB) client cache for the DB-mode trend snapshot.
//
// The in-memory trendEtagStore Map and the localStorage trend-rows cache survive only a single
// session (the Map) or are sync-parse + capacity bound (localStorage). This tier persists the
// server's content ETag alongside the computed trend rows, keyed by (aircraftId, sensitivity), so
// that after a reload the client can issue a conditional GET (If-None-Match) and the server answers
// 304 — rehydrating from the cache instead of re-downloading the multi-MB snapshot body.
//
// The server ETag is authoritative: any content change (reanalyze, GAMI save, profile edit, new
// flight, version bump) changes it, so a stale cached entry simply gets a 200 + fresh rows and is
// overwritten. There is therefore no fragile explicit-invalidation requirement — the conditional
// GET self-heals the cache. A snapshotVersion gate guards against a client render-format change.
//
// Caching is strictly an optimization: every entry point degrades silently to null/false/0 on any
// failure (no IndexedDB, private browsing, serialization error, quota exceeded, ...), so the trends
// view always falls back to a normal fetch and never breaks. Mirrors analysis-cache-store.js.

const TREND_SNAPSHOT_CACHE_DB_NAME = "aeroInsightTrendSnapshotCache";
const TREND_SNAPSHOT_CACHE_STORE_NAME = "trendSnapshots";
const TREND_SNAPSHOT_CACHE_MAX_ENTRIES = 24;

// Permanently false for the session if a snapshot ever fails structured clone, so we stop paying the
// clone cost and stop logging DataCloneError on every write.
let cloneSupported = true;
let cloneProven = false;

function snapshotCacheKey(aircraftId, sensitivity) {
  return `${aircraftId}|${sensitivity ?? ""}`;
}

function openTrendSnapshotCacheStore() {
  if (!globalThis.indexedDB) return Promise.resolve(null);
  return new Promise((resolve) => {
    let request;
    try {
      request = indexedDB.open(TREND_SNAPSHOT_CACHE_DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    request.addEventListener("upgradeneeded", () => {
      const store = request.result.createObjectStore(TREND_SNAPSHOT_CACHE_STORE_NAME, { keyPath: "key" });
      store.createIndex("lastAccess", "lastAccess");
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => resolve(null));
    request.addEventListener("blocked", () => resolve(null));
  });
}

async function withTrendSnapshotCacheStore(mode, callback) {
  const database = await openTrendSnapshotCacheStore();
  if (!database) return null;
  return new Promise((resolve) => {
    let transaction;
    try {
      transaction = database.transaction(TREND_SNAPSHOT_CACHE_STORE_NAME, mode);
    } catch {
      database.close();
      resolve(null);
      return;
    }
    const store = transaction.objectStore(TREND_SNAPSHOT_CACHE_STORE_NAME);
    const done = (value) => {
      database.close();
      resolve(value);
    };
    transaction.addEventListener("error", () => done(null));
    transaction.addEventListener("abort", () => done(null));
    callback(store, done);
  });
}

// Fire-and-forget LRU bump on read; failures are ignored.
function touchLastAccess(key) {
  withTrendSnapshotCacheStore("readwrite", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => {
      const record = request.result;
      if (record) {
        record.lastAccess = Date.now();
        store.put(record);
      }
      done(true);
    });
    request.addEventListener("error", () => done(null));
  }).catch(() => {});
}

// Returns { etag, rows } for a still-valid cached snapshot, or null. Two gates beyond presence:
//  - snapshotVersion: a client render-format change must not surface stale-shaped rows.
//  - exclusionSignature: the cached rows are ALREADY filtered by the client's trend-exclusion
//    preference, but the SERVER content ETag is computed from the full supported flight set (client
//    exclusions are never sent to the server). So a 304 only proves the server body is unchanged, not
//    that the exclusion state matches. We therefore reject (cache miss -> fresh 200) when the caller's
//    current exclusion signature differs from the one the cached rows were filtered under — otherwise
//    re-including a previously-excluded flight would 304 and silently drop it from the view.
export async function readCachedTrendSnapshot(aircraftId, sensitivity, expectedVersion, expectedExclusionSignature = null) {
  if (!aircraftId || !globalThis.indexedDB) return null;
  const key = snapshotCacheKey(aircraftId, sensitivity);
  const record = await withTrendSnapshotCacheStore("readonly", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => done(request.result ?? null));
    request.addEventListener("error", () => done(null));
  }).catch(() => null);
  if (!record) return null;
  if (expectedVersion != null && record.snapshotVersion !== expectedVersion) return null;
  if (expectedExclusionSignature != null && (record.exclusionSignature ?? "") !== expectedExclusionSignature) return null;
  if (typeof record.etag !== "string" || !record.etag) return null;
  if (!Array.isArray(record.rows)) return null;
  touchLastAccess(key);
  return { etag: record.etag, rows: record.rows, snapshotVersion: record.snapshotVersion };
}

// Evict oldest entries (by lastAccess) until at most MAX remain. Runs inside the same readwrite
// transaction as the preceding put.
function pruneToMaxEntries(store, done) {
  const countRequest = store.count();
  countRequest.addEventListener("success", () => {
    const overflow = countRequest.result - TREND_SNAPSHOT_CACHE_MAX_ENTRIES;
    if (overflow <= 0) {
      done(true);
      return;
    }
    let removed = 0;
    const cursorRequest = store.index("lastAccess").openCursor();
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result;
      if (!cursor || removed >= overflow) {
        done(true);
        return;
      }
      cursor.delete();
      removed += 1;
      cursor.continue();
    });
    cursorRequest.addEventListener("error", () => done(null));
  });
  countRequest.addEventListener("error", () => done(null));
}

export async function writeCachedTrendSnapshot(aircraftId, sensitivity, { etag, rows, snapshotVersion, exclusionSignature } = {}) {
  if (!aircraftId || typeof etag !== "string" || !etag || !Array.isArray(rows)) return false;
  if (!cloneSupported) return false;
  // Prove (once) the rows survive structured clone before persisting; converts a would-be
  // DataCloneError on store.put into a one-time, silent opt-out for the session.
  if (!cloneProven) {
    try {
      if (typeof structuredClone === "function") structuredClone(rows);
      cloneProven = true;
    } catch {
      cloneSupported = false;
      return false;
    }
  }
  const record = {
    key: snapshotCacheKey(aircraftId, sensitivity),
    aircraftId,
    sensitivity: sensitivity ?? "",
    etag,
    rows,
    snapshotVersion: snapshotVersion ?? null,
    exclusionSignature: exclusionSignature ?? "",
    cachedAt: new Date().toISOString(),
    lastAccess: Date.now(),
  };
  const saved = await withTrendSnapshotCacheStore("readwrite", (store, done) => {
    let putRequest;
    try {
      putRequest = store.put(record);
    } catch {
      cloneSupported = false;
      done(null);
      return;
    }
    putRequest.addEventListener("success", () => pruneToMaxEntries(store, done));
    putRequest.addEventListener("error", () => done(null));
  }).catch(() => null);
  return Boolean(saved);
}

// Drop every persisted snapshot for an aircraft (all sensitivities). The ETag would self-heal a
// stale entry anyway; this is hygiene called wherever the trend rows are explicitly cleared. The
// object store keys on (aircraftId|sensitivity), so we cursor-scan the <=24 records. Returns the
// number removed; degrades silently to 0.
export async function deleteCachedTrendSnapshotsForAircraft(aircraftId) {
  if (!aircraftId) return 0;
  const removed = await withTrendSnapshotCacheStore("readwrite", (store, done) => {
    let count = 0;
    const cursorRequest = store.openCursor();
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result;
      if (!cursor) {
        done(count);
        return;
      }
      if (cursor.value?.aircraftId === aircraftId) {
        cursor.delete();
        count += 1;
      }
      cursor.continue();
    });
    cursorRequest.addEventListener("error", () => done(count));
  }).catch(() => 0);
  return removed ?? 0;
}
